import React from 'react'
import { useTranslation } from '@mc/i18n'
import {
  TrackClick,
  TrackingAttributes,
} from '../../../tracking/educatorTracking'

type SocialProvider = 'google' | 'facebook'

interface SocialLoginButtonsProps {
  onSocialLogin: (provider: SocialProvider) => void
  disabled?: boolean
  isGoogleEnabled?: boolean
  isFacebookEnabled?: boolean
}

const GoogleIcon = () => (
  <svg width='15' height='15' viewBox='0 0 15 15' fill='none'>
    <path
      d='M14.5 7.65909C14.5 7.13636 14.4545 6.63636 14.3727 6.15909H7.5V9H11.4545C11.2818 9.93182 10.7636 10.7182 9.97727 11.2318V13.0909H12.3364C13.7182 11.8182 14.5 9.93182 14.5 7.65909Z'
      fill='#4285F4'
    />
    <path
      d='M7.5 14.9091C9.52273 14.9091 11.2091 14.2273 12.3364 13.0909L9.97727 11.2318C9.30909 11.6818 8.47727 11.9545 7.5 11.9545C5.54545 11.9545 3.90909 10.6636 3.31818 8.90909H0.886364V10.8273C2.00455 13.0455 4.54545 14.9091 7.5 14.9091Z'
      fill='#34A853'
    />
    <path
      d='M3.31818 8.90909C3.16818 8.45909 3.08182 7.98182 3.08182 7.5C3.08182 7.01818 3.16818 6.54091 3.31818 6.09091V4.17273H0.886364C0.322727 5.29091 0 6.55909 0 7.5C0 8.44091 0.322727 9.70909 0.886364 10.8273L3.31818 8.90909Z'
      fill='#FBBC05'
    />
    <path
      d='M7.5 3.04545C8.56818 3.04545 9.52273 3.41818 10.2727 4.14545L12.3909 2.02727C11.2045 0.909091 9.52273 0.0909091 7.5 0.0909091C4.54545 0.0909091 2.00455 1.95455 0.886364 4.17273L3.31818 6.09091C3.90909 4.33636 5.54545 3.04545 7.5 3.04545Z'
      fill='#EA4335'
    />
  </svg>
)

const FacebookIcon = () => (
  <svg width='15' height='15' viewBox='0 0 15 15' fill='none'>
    <path
      d='M15 7.5C15 3.35786 11.6421 0 7.5 0C3.35786 0 0 3.35786 0 7.5C0 11.2388 2.74219 14.3493 6.32812 14.9006V9.66797H4.42383V7.5H6.32812V5.84766C6.32812 3.96094 7.44727 2.92578 9.16992 2.92578C9.99609 2.92578 10.8594 3.07617 10.8594 3.07617V4.92188H9.90820C8.96484 4.92188 8.67188 5.50781 8.67188 6.10547V7.5H10.7715L10.4355 9.66797H8.67188V14.9006C12.2578 14.3493 15 11.2388 15 7.5Z'
      fill='#1877F2'
    />
    <path
      d='M10.4355 9.66797L10.7715 7.5H8.67188V6.10547C8.67188 5.50781 8.96484 4.92188 9.90820 4.92188H10.8594V3.07617C10.8594 3.07617 9.99609 2.92578 9.16992 2.92578C7.44727 2.92578 6.32812 3.96094 6.32812 5.84766V7.5H4.42383V9.66797H6.32812V14.9006C6.71484 14.9668 7.10938 15 7.5 15C7.89062 15 8.28516 14.9668 8.67188 14.9006V9.66797H10.4355Z'
      fill='white'
    />
  </svg>
)

export const SocialLoginButtons = ({
  onSocialLogin,
  disabled = false,
  isGoogleEnabled = true,
  isFacebookEnabled = true,
}: SocialLoginButtonsProps) => {
  const { t } = useTranslation()

  const allButtons = [
    {
      provider: 'google' as SocialProvider,
      icon: <GoogleIcon />,
      label: t('emailEntry.continueWithGoogle'),
      isEnabled: isGoogleEnabled,
    },
    {
      provider: 'facebook' as SocialProvider,
      icon: <FacebookIcon />,
      label: t('emailEntry.continueWithFacebook'),
      isEnabled: isFacebookEnabled,
    },
  ]

  const buttons = allButtons.filter(({ isEnabled }) => isEnabled)

  if (buttons.length === 0) {
    return null
  }

  return (
    <TrackingAttributes location='email form'>
      <div className='flex flex-col gap-3 w-full'>
        {buttons.map(({ provider, icon, label }) => (
          <TrackClick key={provider} event='educatorClick' action={label}>
            <button
              type='button'
              onClick={() => onSocialLogin(provider)}
              disabled={disabled}
              className='flex items-center justify-center gap-2 w-full h-12 px-4 pb-[2px] bg-transparent border border-border-black cursor-pointer transition-colors hover:enabled:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <span className='flex items-center justify-center p-[2.5px]'>
                {icon}
              </span>
              <span className='font-sans font-semibold text-sm leading-none text-text-primary tracking-[0.14px]'>
                {label}
              </span>
            </button>
          </TrackClick>
        ))}
      </div>
    </TrackingAttributes>
  )
}
